import {
  AlertTriangle,
  ArrowUpRight,
  Clock,
  FileText,
  GalleryVerticalEnd,
  Layers,
  Maximize,
  Maximize2,
  RectangleHorizontal,
  Sparkles,
  SquareCheckBig,
} from "lucide-react";
import { PostStudyNotebooksByIdHighlightsResponse } from "../../../client";
import { useEffect, useRef, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  ToolCallMessagePart,
  ToolCallMessagePartProps,
} from "@assistant-ui/react";
import { FlashcardsPreview } from "./flashcards/preview";
import { ToolFallback } from "@/components/assistant-ui/tool-fallback";
import { QuizPreview } from "./quiz/preview";
import { SummaryPreview } from "./summary/preview";
import { useNotebookContext } from "../../../hooks/useNotebook";
import { Flashcard } from "./flashcards/Flashcard";
import { FlashcardSet } from "./flashcards/FlashcardSet";
import { FlashcardsPage } from "./flashcards/FlashcardPage";
import { QuizView } from "./quiz/QuizView";
import { QuizPage } from "./quiz/QuizPage";
import { FileSummaryView } from "./summary/SummaryView";
import { BuildingHighlightState } from "./building-highlight";

// =============================================================
// Tool Definitions
// =============================================================
export const HIGHLIGHT_CONFIG = [
  {
    id: "flashcard",
    label: "Flashcards",
    labelLong: "Flashcard Sets",
    configPrompt: {
      makePrompt: ({
        count,
        difficulty,
      }: {
        count: string;
        difficulty: string;
      }) =>
        `The flashcards should be ${difficulty.toLowerCase()} difficulty level with ${count} total flashcards.`,
      options: {
        count: ["5", "10", "15"],
        difficulty: ["Easy", "Medium", "Hard"],
      },
    },
    icon: GalleryVerticalEnd,
    previewComponent: FlashcardsPreview,
    focusedComponent: FlashcardsPage,
    color: {
      text: "text-emerald-800",
      bgDark: "bg-emerald-500/80",
      bg: "bg-emerald-200/90",
      bgLight: "bg-emerald-400/10",
      hoverBg: "hover:bg-emerald-300/70",
      border: "border-emerald-500",
      borderLight: "border-emerald-500/50",
    },
    description: "Generate flashcards from course materials",
    parameters: {
      type: "object",
      properties: {
        topic: {
          type: "string",
          description: "Optional topic to focus on",
        },
        count: {
          type: "number",
          description: "Number of flashcards to generate",
          default: 10,
        },
      },
    },
  },
  {
    id: "quiz",
    label: "Quiz",
    labelLong: "Practice Quizzes",
    configPrompt: {
      makePrompt: ({
        count,
        difficulty,
      }: {
        count: string;
        difficulty: string;
      }) =>
        `The quiz should be ${difficulty.toLowerCase()} difficulty with ${count} total questions.`,
      options: {
        count: ["5", "10", "15"],
        difficulty: ["Easy", "Medium", "Hard"],
      },
    },
    icon: SquareCheckBig,
    color: {
      text: "text-orange-700",
      bgDark: "bg-orange-500/80",
      bg: "bg-orange-200/90",
      bgLight: "bg-orange-400/10",
      hoverBg: "hover:bg-orange-300/70",
      border: "border-orange-500",
      borderLight: "border-orange-500/50",
    },
    description: "Create a quiz from course materials",
    previewComponent: QuizPreview,
    focusedComponent: QuizPage,
  },
  {
    id: "summary",
    label: "Summary",
    labelLong: "File Summaries",
    configPrompt: {
      makePrompt: ({ format }: { format: string }) =>
        `The summary should be very ${format.toLowerCase()} and highlight the key points.`,
      options: {
        format: ["Brief", "Detailed"],
      },
    },
    icon: FileText,
    color: {
      text: "text-blue-700",
      bgDark: "bg-blue-500/80",
      bg: "bg-blue-200/90",
      bgLight: "bg-blue-400/10",
      hoverBg: "hover:bg-blue-300/70",
      border: "border-blue-500",
      borderLight: "border-blue-500/50",
    },
    description: "Generate a summary of course materials",
    previewComponent: SummaryPreview,
    focusedComponent: FileSummaryView,
  },
] as const;

export type HighlightCategory = (typeof HIGHLIGHT_CONFIG)[number]["id"];

export type HighlightMaterials = PostStudyNotebooksByIdHighlightsResponse;

export type HighlightComponentProps<T = any> = {
  result: HighlightMaterials;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onFinished?: (result: HighlightMaterials) => void;
};

// =============================================================
// Tool Result UI Wrapper
// =============================================================

type ToolResultWrapperProps = {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: (typeof HIGHLIGHT_CONFIG)[number]["color"];
  isLoading: boolean;
  result?: HighlightMaterials;
  children?: React.ReactNode;
  onFinished?: (result: HighlightMaterials) => void;
};
export const ToolResultWrapper: React.FC<ToolResultWrapperProps> = ({
  label,
  icon: Icon,
  color,
  isLoading,
  result,
  children,
  onFinished,
}) => {
  const [hasFiredFinish, setHasFiredFinish] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const { navigatePage } = useNotebookContext();

  useEffect(() => {
    if (result && !hasFiredFinish) {
      onFinished?.(result);
      setHasFiredFinish(true);
    }
  }, [result, hasFiredFinish, onFinished]);

  useEffect(() => {
    const checkWidth = () => {
      if (headerRef.current) {
        const width = headerRef.current.offsetWidth;
        setIsNarrow(width < 400);
      }
    };

    checkWidth();
    const resizeObserver = new ResizeObserver(checkWidth);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    window.addEventListener("resize", checkWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", checkWidth);
    };
  }, []);

  const showArtifact = !isLoading && !!result;

  return (
    <div
      className={cn(
        "rounded-2xl border-2 transition-all duration-300 overflow-hidden mb-5",
        color.border,
        isLoading ? "shadow-lg" : "shadow-xl"
      )}
    >
      {/* Header - solid, tangible feeling */}
      <div
        ref={headerRef}
        className={cn("px-6 py-5 border-b-2", color.bgLight, color.border)}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <div
              className={cn(
                "flex h-12 w-12 items-center justify-center rounded-xl text-white",
                // color.text,
                color.bgDark
              )}
            >
              <Icon className="h-6 w-6" />
            </div>

            <div className="leading-tight">
              <div
                className={cn("text-lg font-bold tracking-tight text-theme-0")}
              >
                {label}
              </div>
              <div
                className={cn(
                  "text-sm font-semibold flex items-center gap-2 text-theme-0"
                )}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>Creating your materials...</span>
                  </>
                ) : (
                  <>
                    <span className="inline-block h-2 w-2 rounded-full bg-green-500 shadow-sm" />
                    <span>Ready</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          {!isLoading && (
            <button
              type="button"
              onClick={() => navigatePage({ type: "highlight", value: result })}
              className={cn(
                "inline-flex items-center gap-2 cursor-pointer",
                "h-10 px-3 rounded-xl",
                "font-semibold text-sm",
                color.bgDark,
                "text-white",
                "hover:opacity-90 hover:scale-[1.02]",
                "transition-all duration-200",
                "shadow-lg text-xs"
              )}
              aria-label={`Open ${label}`}
              title={`Open ${label}`}
            >
              <span>Open{isNarrow ? "" : ` ${label}`}</span>
              <ArrowUpRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* ============================= */}
      {/* Loading surface */}
      {/* ============================= */}
      {isLoading && <BuildingHighlightState color={color} />}

      {/* ============================= */}
      {/* Artifact surface (expand animation) */}
      {/* ============================= */}
      <div
        className={cn(
          "grid transition-all duration-500 ease-out bg-theme-0",
          showArtifact
            ? "grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0"
        )}
      >
        <div className="min-w-0 overflow-hidden">
          <div className="">
            <div className="bg-theme-0 shadow-inner overflow-hidden">
              <div className="max-h-[280px] overflow-y-auto">
                {/* Metadata - feels like a card header */}
                {result && (
                  <div className="bg-theme-0 border-b-2 border-theme px-6 py-4">
                    <h3 className="text-lg font-bold text-theme-0 tracking-tight mb-3">
                      {result.name}
                    </h3>

                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-semibold text-theme-1">
                      {result.metadata?.document_ids?.length ? (
                        <span className="inline-flex items-center gap-2 bg-theme-1 px-3 py-1.5 rounded-lg">
                          <FileText className="h-4 w-4" />
                          <span>
                            {result.metadata.document_ids.length} source
                            {result.metadata.document_ids.length > 1 ? "s" : ""}
                          </span>
                        </span>
                      ) : null}

                      {result.created_at && (
                        <span className="inline-flex items-center gap-2 bg-theme-1 px-3 py-1.5 rounded-lg">
                          <Clock className="h-4 w-4" />
                          <time
                            dateTime={result.created_at}
                            title={new Date(result.created_at).toLocaleString()}
                          >
                            {new Date(result.created_at).toLocaleDateString(
                              undefined,
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              }
                            )}
                          </time>
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Generated preview - content area */}
                <div className="px-6 py-5 bg-theme-0">{children}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================
// Highlight UI Wrapper
// =============================================================

export const HighlightToolResult: React.FC<ToolCallMessagePartProps> = (
  props
) => {
  const { addHighlight, navigatePage } = useNotebookContext();
  useEffect(() => {
    if (props.result) {
      setTimeout(() => {
        addHighlight(props.result as HighlightMaterials);
      }, 800);
    }
  }, [props.result]);
  const config = HIGHLIGHT_CONFIG.find((h) => h.label === props.toolName);
  if (!config) return <ToolFallback {...props} />;

  const PreviewComponent = config.previewComponent;

  return (
    <ToolResultWrapper
      label={config.label}
      icon={config.icon}
      color={config.color}
      isLoading={props.status.type === "running"}
      result={props.result as any}
      onFinished={(r) => {
        console.log("Finished generating:", r);
      }}
    >
      {props.result && !props.isError && (
        <PreviewComponent
          result={props.result as HighlightMaterials}
          isExpanded={false}
          onToggleExpand={() =>
            navigatePage({
              type: "highlight",
              value: props.result as HighlightMaterials,
            })
          }
        />
      )}

      {props.isError && (
        <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-md bg-destructive/10 text-destructive">
              <AlertTriangle className="h-4 w-4" />
            </div>

            {/* Content */}
            <div className="flex-1">
              <div className="text-sm font-medium text-foreground">
                Generation failed
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                Bettercampus ran into an error while generating this result. You
                can try again or adjust your input.
              </div>
            </div>
          </div>
        </div>
      )}
    </ToolResultWrapper>
  );
};
